/* UI component constants
 *
 *
 */
import { isDarkMode } from '../utils/helpers';

export const socialColor = {
  GOOGLE: '#4285F4',
};

export const getStatusColor = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    success: '#0DB800',
    error: '#DD0B24',
    caution: '#FF7C00',
    notification: '#FF0000',
    default: !themeDarkMode ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.15)',
  };
};

export const space = {
  XS: 4,
  SM: 8,
  MD: 10,
  LG: 20,
  XL: 32,
};

export const rawMediaQuery = (mq: any): any => mq.replace('@media ', '');

export const getTextColor = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    // Grey colors. Use for normal body and Title text.
    LIGHTER: { color: !themeDarkMode ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.4)' },
    NORMAL: { color: !themeDarkMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)' },
    DARK: { color: !themeDarkMode ? 'rgba(0, 0, 0, 0.65)' : '#AFB0B1' },
    DARKER: { color: !themeDarkMode ? 'rgba(0, 0, 0, 0.8)' : '#cfcfcf' },
    DARKEST: { color: !themeDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)' },
    DARK_2: { color: !themeDarkMode ? '#595959' : '#AFB0B1' },
    DARK_3: { color: !themeDarkMode ? '#F6F4F4' : '#CFCFCF' },
    DARK_4: { color: !themeDarkMode ? '#212121' : '#F6F4F4' },
    //Misc. Colors
    BRAND: { color: !themeDarkMode ? '#3e79a5' : '#cfcfcf' },
    RED: { color: 'rgb(204, 84, 84)' },
    LINK: { color: !themeDarkMode ? '#1383d2' : '#AFB0B1' },
    LIGHT_GRAY: { color: themeDarkMode ? '#AFB0B1' : '#595959' },
    WHITE_GRAY: { color: themeDarkMode ? '#fff' : '#595959' },
    BLUE: { color: themeDarkMode ? '#71B5E4' : '#2B8FD7' },
    SUCCESS: { color: themeDarkMode ? '#5EBE5E' : '#24A83A' },
    TITLE: {
      color: themeDarkMode ? '#F6F4F4' : '#333',
    },
  };
};

export const getColor = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    GREY_1: 'rgba(0, 0, 0, 0.033)',
    GREY_2: !themeDarkMode ? 'rgba(0, 0, 0, 0.44)' : '#cfcfcf',
    GREY_3: 'rgba(0, 0, 0, 0.066)',
    GREY_LIGHTEST: !themeDarkMode ? 'rgba(0, 0, 0, 0.02)' : 'rgba(0, 0, 0, 0.85)',

    //Brand colors
    BRAND_1: !themeDarkMode ? '#3e79a5' : '#6c6c6c',
    BRAND_2: '#1383d2',
    BRAND_1_DARK: '#3e79a5',
    BRAND_1_ONLINE: '#32CD32',
    BRAND_BLUE_BG: '#D6E5FD',

    //
    EMAIL: !themeDarkMode ? '#263445' : '#cfcfcf',
  };
};

export const size = {
  SIZE_40X40: {
    height: '40px',
    width: '40px',
  },
  SIZE_60X60: {
    height: '60px',
    width: '60px',
  },
  SIZE_80X80: {
    height: '80px',
    width: '80px',
  },
  SIZE_90X90: {
    height: '90px',
    width: '90px',
  },
};

export const type = {
  XS: { fontSize: '12px' },
  SM: { fontSize: '13px' },
  MD: { fontSize: '16px' },
  LG: { fontSize: '20px' },
  XL: { fontSize: '26px' },
  XXL: { fontSize: '33px' },
  XXXL: { fontSize: '53px' },

  //weights
  THIN: { fontWeight: '300' },
  NORMAL: { fontWeight: '400' },
  MEDIUM: { fontWeight: '600' },
  BOLD: { fontWeight: '700' },

  title: {
    XS: { fontSize: '18px' },
    SM: { fontSize: '20px' },
    MD: { fontSize: '24px' },
    LG: { fontSize: '30px' },
    XL: { fontSize: '36px' },
  },
};

export const getTextType = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    header: {
      fontSize: '20px',
      fontWeight: 600,
      color: !themeDarkMode ? '#212121' : '#cfcfcf',
      textTransform: 'capitalize',
    },
    text: {
      fontSize: '16px',
      fontWeight: 400,
      color: !themeDarkMode ? 'rgba(0,0,0,0.65)' : '#AFB0B1',
    },
    caption: {
      fontSize: '13px',
      fontWeight: 400,
      color: !themeDarkMode ? 'rgba(0,0,0,0.65)' : '#AFB0B1',
    },
    processingHeader: {
      ...type.XL,
      ...type.MEDIUM,
      color: !themeDarkMode ? 'rgba(0,0,0,0.8)' : '#cfcfcf',
    },
    blockHeader: {
      fontWeight: 600,
      color: !themeDarkMode ? '#212121' : '#cfcfcf',
    },
    toggleButton: {
      fontSize: '13px',
      fontWeight: 600,
      letterSpacing: '-0.26px',
    },
  };
};

export const leading = {
  // Relative Units
  BASE: { lineHeight: '1.4' },
  TIGHT: { lineHeight: '1.2' },
  TIGHTER: { lineHeight: '1.1' },
  TIGHTEST: { lineHeight: '1' },
  LOOSE: { lineHeight: '1.6' },
  INHERIT: { lineHeight: 'inherit' },

  // Add absolute units here.
};

export const spacing = {
  SM: 10,
  MD: 15,
  LG: 22,
  XL: 32,

  MARGIN_0: { margin: '0' },
  MARGIN_SM: { margin: '10px' },
  MARGIN_MD: { margin: '16px' },

  MARGIN_BOTTOM_XS: { marginBottom: '4px' },
  MARGIN_BOTTOM_SM: { marginBottom: '10px' },
  MARGIN_BOTTOM_MD: { marginBottom: '16px' },
  MARGIN_BOTTOM_LG: { marginBottom: '22px' },
  MARGIN_BOTTOM_XLG: { marginBottom: '24px' },
  MARGIN_BOTTOM_XL: { marginBottom: '32px' },

  MARGIN_TOP_XS: { marginTop: '4px' },
  MARGIN_TOP_SM: { marginTop: '10px' },
  MARGIN_TOP_MD: { marginTop: '16px' },
  MARGIN_TOP_LG: { marginTop: '22px' },
  MARGIN_TOP_XL: { marginTop: '32px' },

  MARGIN_V_XS: { margin: '4px 0' },
  MARGIN_V_SM: { margin: '10px 0' },
  MARGIN_V_MD: { margin: '22px 0' },
  MARGIN_V_LG: { margin: '32px 0' },

  MARGIN_LEFT_XS: { marginLeft: '4px' },
  MARGIN_LEFT_SM: { marginLeft: '10px' },
  MARGIN_LEFT_MD: { marginLeft: '16px' },
  MARGIN_LEFT_LG: { marginLeft: '22px' },
  MARGIN_LEFT_XL: { marginLeft: '32px' },

  MARGIN_LEFT_NEG_XL: { marginLeft: '-32px' },

  MARGIN_RIGHT_XS: { marginRight: '4px' },
  MARGIN_RIGHT_SM: { marginRight: '10px' },
  MARGIN_RIGHT_MD: { marginRight: '16px' },
  MARGIN_RIGHT_LG: { marginRight: '22px' },
  MARGIN_RIGHT_XL: { marginRight: '32px' },

  MARGIN_H_MD: { margin: '0 22px' },
  MARGIN_H_LG: { margin: '0 32px' },

  MARGIN_RIGHT_SM_NEG: { marginRight: -10 },

  PADDING_0: { padding: '0' },
  PADDING_XS: { padding: '4px' },
  PADDING_SM: { padding: '10px' },
  PADDING_MD: { padding: '16px' },
  PADDING_LG: { padding: '22px' },

  // padding

  PADDING_H_SM: { padding: '0 10px' },
  PADDING_H_MD: { padding: '0 22px' },

  PADDING_BOTTOM_XS: { paddingBottom: '4px' },
  PADDING_BOTTOM_SM: { paddingBottom: '10px' },
  PADDING_BOTTOM_MD: { paddingBottom: '16px' },
  PADDING_BOTTOM_LG: { paddingBottom: '22px' },
  PADDING_BOTTOM_XL: { paddingBottom: '32px' },

  PADDING_TOP_XS: { paddingTop: '4px' },
  PADDING_TOP_SM: { paddingTop: '10px' },
  PADDING_TOP_MD: { paddingTop: '16px' },
  PADDING_TOP_LG: { paddingTop: '22px' },
  PADDING_TOP_XL: { paddingTop: '32px' },

  PADDING_V_XS: { padding: '4px 0' },
  PADDING_V_SM: { padding: '10px 0' },
  PADDING_V_MD: { padding: '22px 0' },
  PADDING_V_XL: { padding: '32px 0' },

  PADDING_LEFT_XS: { paddingLeft: '4px' },
  PADDING_LEFT_SM: { paddingLeft: '10px' },
  PADDING_LEFT_MD: { paddingLeft: '16px' },
  PADDING_LEFT_LG: { paddingLeft: '22px' },
  PADDING_LEFT_XL: { paddingLeft: '32px' },

  PADDING_RIGHT_XS: { paddingRight: '4px' },
  PADDING_RIGHT_SM: { paddingRight: '10px' },
  PADDING_RIGHT_MD: { paddingRight: '16px' },
  PADDING_RIGHT_LG: { paddingRight: '22px' },
  PADDING_RIGHT_XL: { paddingRight: '32px' },

  //spacing (Margin) utils
  //spacing (inter - Padding) utils
};

export const getBorders = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    DEFAULT: `1px solid ${!themeDarkMode ? '#DAE1E9' : '#6c6c6c'}`,
    DARKER: `1px solid ${!themeDarkMode ? 'rgba(0,0,0,0.15)' : '#848484'}`,
    BRAND: `1px solid ${!themeDarkMode ? '#1383d2' : '#2b8fd7'}`,
    ERROR: `1px solid ${!themeDarkMode ? 'rgb(204, 84, 84)' : '#E21D4A'}`,
    FOCUS: `1px solid ${!themeDarkMode ? '#1383d2' : '#cfcfcf'}`,
    DASHED: `1px dashed ${!themeDarkMode ? '#D8D8D8' : '#D8D8D8'}`,
    PRIMARY: `1px solid ${themeDarkMode ? '#848484' : '#DAE1E9'}`,
    PRIMARY_DASHED: `1px dashed ${themeDarkMode ? '#848484' : '#DAE1E9'}`,
  };
};

export const box = {
  border: getBorders().DEFAULT,
  boxShadow: '0 1px 4px 0 rgba(0,0,0,.04)',
  borderRadius: '6px',
};

/**
 * FORM Components
 * Use these until we create common form Components
 */

export const textInput = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '0 15px',
  height: '38px',
  outline: 'none',
  borderRadius: '3px',
  border: getBorders().DARKER,
  ...type.MD,
};

export const comboBox = {
  appearance: 'none',
  boxSizing: 'border-box',
  background: '0 0',
  border: getBorders().DARKER,
  padding: '0 15px',
  height: '38px',
  outline: 'none',
  borderRadius: '3px',
  ...type.SM,
};

export const headline = {
  color: getTextColor().BRAND.color,
  fontSize: '22px',
  fontWeight: 'normal',
  margin: '0 0 16px 0',
};

export const getBackgroundTheme = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    surface: themeDarkMode ? '#323232' : '#F6F4F4',
    fsModal: themeDarkMode ? '#020d15' : '#ffffff',
    block: !themeDarkMode ? '#F6F4F4' : '#323232',
    block2: !themeDarkMode ? '#fff' : '#212121',
    block3: !themeDarkMode ? '#fff' : '#323232',
    paymentBlock: !themeDarkMode ? '#F5F7FA' : '#212121',
    paymentHighlight: !themeDarkMode ? '#EBFAF8' : '#4D5D6D',
    navBG: !themeDarkMode ? '#444444' : 'rgba(255, 255, 255, 0.07)',
    fill: !themeDarkMode ? '#E2C92D' : '#eeeeee',
    gallery: !themeDarkMode ? '#f4f6f9' : '#041a2a',
    personalInfo: !themeDarkMode ? '#ffffff' : '#212121',
    personalInfoBG: !themeDarkMode ? 'linear-gradient(180deg, #E4F8F6 40%, #FFF 100%)' : '#212121',
    defaultText: !themeDarkMode ? '#eeeeee' : '#71B5E4',
    greyText: !themeDarkMode ? '#3c4858' : '#cfcfcf',
    billingHeading: !themeDarkMode ? '#212121' : '#F6F4F4',
    selectedPackage: !themeDarkMode ? '#D6E5FD' : '#041a2a',
    creditText: !themeDarkMode ? '#2490de' : '#cfcfcf',
    defaultBnW: !themeDarkMode ? '#E21D4A' : '#FFFFFF',
    creditColor: !themeDarkMode ? 'linear-gradient(93deg, #10CBBB 0%, #2B8FD7 100%)' : '#d06369',
    placeHolderBG: themeDarkMode ? '#020d15' : 'linear-gradient(to right, #EFF1F3 8%, #FBFBFD 18%, #EFF1F3 33%)',
    emText: themeDarkMode ? '#cfcfcf' : 'rgba(0, 0, 0, 0.6)',
    completionBanner: themeDarkMode ? '#020d15' : '#E6F4FD',
    footerLinks: themeDarkMode ? '#CFCFCF' : '#595959',
    cardDetails: themeDarkMode ? '#AFB0B1' : 'rgba(255, 255, 255, 0.7)',
    blankDetails: themeDarkMode ? '#AFB0B1' : '#595959',
    iconFill: themeDarkMode ? '#AFB0B1' : '#C9F3EF',
    abandonmentBG: themeDarkMode ? '#020d15' : '#EAF4FB',
    AddMedia: themeDarkMode ? '#212121' : '#EAF4FB',
    orderSummary: !themeDarkMode ? '#ffffff' : '#212121',
    paymetPackage: !themeDarkMode ? '#ffffff' : '#041a2a',
    packageCard: !themeDarkMode ? '#ffffff' : '#041a2a',
    headerCTA: '#212121',
    errorModal: themeDarkMode ? '#2A0404' : '#FFF8F8',
    loader: themeDarkMode ? 'rgba(4, 26, 42, 0.7)' : 'rgba(255, 255, 255, 0.7)',
    changeEmailBottomHeadline: themeDarkMode ? '#ffffff' : '#0D5C93',
  };
};

export const getIconsTheme = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    profileCompleteness: themeDarkMode ? '#8392A7' : 'rgba(0, 0, 0, 0.45)',
  };
};

export const getShadowTheme = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    footer: themeDarkMode ? '0 -1px 2px 0px rgb(189 192 196 / 50%)' : '0 -1px 2px 0px rgba(0, 0, 0, 0.2)',
    header: themeDarkMode ? '0 2px 2px -2px rgba(232, 232, 232, 0.2)' : '0 2px 2px -2px rgba(0, 0, 0, 0.2)',
    footerLogo: themeDarkMode
      ? '0 -3px 0.5px -0.5px rgb(189 192 196 / 50%)'
      : '0 -3px 0.8px -0.2px rgb(0, 0, 0,  0.2);',
  };
};

export const getButtonTheme = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    hoverFocus: !themeDarkMode ? 'rgb(28, 115, 177)' : 'rgba(28, 115, 177, 0.8)',
    active: !themeDarkMode ? 'rgb(21, 86, 133)' : 'rgba(21, 86, 133, 0.8)',
    disabled: !themeDarkMode ? 'rgb(167, 210, 241)' : 'rgba(167, 210, 241, 0.8)',
    videoCall: themeDarkMode ? '#0F4719' : '#EAF7EC',
    buttonText: themeDarkMode ? '#020d15' : '#F6F4F4',
    buttonText2: themeDarkMode ? '#000' : '#FFF',
    GreyAndWhite: themeDarkMode ? '#F6F4F4' : 'rgb(255, 255, 255)',
    DarkButton: themeDarkMode ? '#000000' : 'rgba(0, 0, 0, 0.8)',
    payButton: themeDarkMode ? '#1383d2' : '#020d15',
    defaultBorder: `1px solid ${themeDarkMode ? '#6c6c6c' : '#808080'}`,
    darkReverse: themeDarkMode ? '#F6F4F4' : '#808080',
    darkReverse2: themeDarkMode ? '#F6F4F4' : '#E2C92D',
    MenuGreyAndWhite: themeDarkMode ? 'rgba(255, 255, 255, 0.07)' : 'rgb(255, 255, 255)',
    sideBarHeader: themeDarkMode ? '#71B5E4' : '#0D5C93',
    primaryRed: {
      background: '#E21D4A',
      border: '#E21D4A',
      text: '#FFF',
      hover: {
        text: '#FFF',
        background: '#ED587A',
        border: '#ED587A',
      },
      disabled: {
        text: themeDarkMode ? '#DD8F93' : '#FFFFFF',
        background: themeDarkMode ? '#961A20' : '#DEDEDE',
        border: themeDarkMode ? '#961A20' : '#DEDEDE',
      },
      active: {
        text: '#EB6180',
        background: '#C0103E',
        border: '#C0103E',
      },
    },
    secondaryRed: {
      background: themeDarkMode ? 'transparent' : '#fff',
      border: themeDarkMode ? '#D06369' : '#E21D4A',
      text: themeDarkMode ? '#D06369' : '#E21D4A',
      hover: {
        text: themeDarkMode ? '#FFF' : '#E21D4A',
        background: themeDarkMode ? '#E21D4A' : '#F9D2DB',
        border: themeDarkMode ? '#E21D4A' : '#E21D4A',
        boxShadow: 'none',
      },
      disabled: {
        text: themeDarkMode ? '#711318' : '#C9C9C9',
        border: themeDarkMode ? '#711318' : '#C9C9C9',
        background: themeDarkMode ? '#711318' : '#FFF',
      },
      active: {
        text: themeDarkMode ? '#CD585E' : '#EB6180',
        background: themeDarkMode ? '#711318' : '#C0103E',
        border: themeDarkMode ? '#711318' : '#C0103E',
        boxShadow: 'none',
      },
    },
    secondaryBlue: {
      background: themeDarkMode ? 'transparent' : '#fff',
      border: themeDarkMode ? '#60ABE1' : '#2B8FD7',
      text: themeDarkMode ? '#60ABE1' : '#2B8FD7',
      hover: {
        text: themeDarkMode ? '#FFF' : '#2B8FD7',
        background: themeDarkMode ? '#2272AC' : '#e1f3ff',
        border: themeDarkMode ? '#2272AC' : '#2B8FD7',
      },
      disabled: {
        text: themeDarkMode ? '#1A5681' : '#CAE3F5',
        border: themeDarkMode ? '#1A5681' : '#CAE3F5',
        background: themeDarkMode ? '#1A5681' : '#CAE3F5',
      },
      active: {
        text: themeDarkMode ? '#60ABE1' : '#e1f3ff',
        background: themeDarkMode ? '#1A5681' : '#2B8FD7',
        border: themeDarkMode ? '#1A5681' : '#2B8FD7',
      },
    },
    loadMore: {
      background: themeDarkMode ? 'transparent' : '#FFF',
      text: themeDarkMode ? '#C9C9C9' : '#808080',
      border: themeDarkMode ? '#C9C9C9' : '#808080',
    },
    gradientTeal: {
      background: 'linear-gradient(93deg, #10CBBB 0%, #2B8FD7 100%)',
      text: '#FFF',
      hover: {
        text: '#FFF',
        background: '#34CABB',
      },
      active: {
        text: '#9FEAE4',
        background: '#01B4B1',
      },
    },
  };
};

export const getSlidebarTheme = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    listHover: themeDarkMode ? '#020d15' : '#f3f3f3',
    headerBG: themeDarkMode ? '#041a2a' : '#f2f3f5',
    headerText: themeDarkMode ? '#f2f3f5' : 'rgba(0,0,0,.8)',
  };
};

export const getInputTheme = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    clearBtn: themeDarkMode ? 'transparent' : '#FFFFFF',
    backgroundInput: themeDarkMode ? '#323232' : '#FFFFFF',
    colorInput: themeDarkMode ? '#FFFFFF' : '#000000',
    checkedInput: themeDarkMode ? 'rgba(255, 255, 255, 0.07)' : '#1383d2',
    textInput: themeDarkMode ? '#AFB0B1' : '#595959',
    textInputPlaceholder: themeDarkMode ? '#AFB0B1' : '#C9C9C9',
  };
};

export const getSvgTheme = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    svgColor: themeDarkMode ? '#FFF !important' : '#000 !important',
    arrowBG: themeDarkMode ? 'rgba(4, 26, 42, 0.75)' : 'rgba(51, 51, 51, 0.75)',
  };
};

export const getCreditCardInputField = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    backgroundInput: themeDarkMode ? 'transparent' : '#FFFFFF',
  };
};

export const getRheostatTheme = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    bgColor: themeDarkMode ? '#020d15' : '#2490de',
  };
};

export const getPatternModal = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    bgGradientDark: themeDarkMode ? '#212121' : 'linear-gradient(180deg, #E4F8F6 0%, #FFF 100%) white',
    bgGradient: themeDarkMode
      ? 'linear-gradient(180deg, #1A5681 0%, #020D15 100%)'
      : 'linear-gradient(180deg, #E4F8F6 0%, #FFF 100%) white',
    borderStyle: themeDarkMode ? '2px solid #848484' : 'solid 3px #ffffff',
    headerColor: themeDarkMode ? '#AFB0B1' : '#595959',
    amtColor: themeDarkMode ? '#cfcfcf' : '#3e79a5',
    linkBlue: themeDarkMode ? '#71B5E4' : '#0D5C93',
    linkGrey: themeDarkMode ? '#CAE3F5' : '#8392A7',
    titleColor: '#E21D4A',

    // cancel button
    cancelBlue: themeDarkMode ? '#71B5E4' : '#2B8FD7',
    cancelBG: themeDarkMode ? 'transparent' : '#FFFFFF',
    cancelBorder: themeDarkMode ? '2px solid #71B5E4' : '2px solid #2B8FD7',

    // decline button
    declineRed: themeDarkMode ? '#71B5E4' : '#E21D4A',
    declineBG: themeDarkMode ? 'transparent' : '#FFFFFF',
    declineBorder: themeDarkMode ? '1px solid #71B5E4' : '1px solid #E21D4A',
  };
};

export const getButtonIcons = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    borderStyle: themeDarkMode ? '2px solid #585858' : '2px solid #FFFFFF',
    borderStyleHover: '3px solid #FFF',
    borderCrashOutStyle: !themeDarkMode ? '3px solid rgba(255, 255, 255, 1)' : '3px solid #585858',
  };
};

export const getAmountButtonTheme = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    backgroundStyle: !themeDarkMode ? '#F6F4F4' : '#212121',
    borderStyle: !themeDarkMode ? '2px solid #FFFFFF' : '2px solid #585858',
    borderHoverStyle: '2px solid #FFF',
    color: themeDarkMode ? '#F6F4F4' : '#0D5C93',
  };
};

export const getCompletionTheme = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    containerBG: themeDarkMode ? '#212121' : '#F6F4F4',
    itemBG: themeDarkMode ? '#424242' : '#FFFFFF',
    containerBorder: themeDarkMode ? 'solid 1px #113956' : 'solid 1px #EAF4FB',
  };
};

export const getIdvTheme = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    successIDV: themeDarkMode ? '#082D48' : '#FFFFFF',
    textColor: themeDarkMode ? '#FFFFFF' : '#2B8FD7',
    gradientBG: themeDarkMode
      ? 'linear-gradient(#020D15, #020D15) padding-box, linear-gradient(93deg, #10CBBB 0%, #2B8FD7 100%) border-box'
      : 'linear-gradient(white, white) padding-box, linear-gradient(93deg, #10CBBB 0%, #2B8FD7 100%) border-box',
    sideBarGradientBG: themeDarkMode
      ? 'linear-gradient(#020D15, #020D15) padding-box, linear-gradient(93deg, #10CBBB 0%, #2B8FD7 100%) border-box'
      : 'linear-gradient(white, white) padding-box, linear-gradient(93deg, #10CBBB 0%, #2B8FD7 100%) border-box',
    btnBG: themeDarkMode ? '#020d15' : '#EAF4FB',
  };
};

export const getPaymentTheme = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    SUMMARY: { color: !themeDarkMode ? '#353946' : '#cfcfcf', borderColor: !themeDarkMode ? '#DAE1E9' : '#848484' },
    SUMMARY_SECONDARY: { borderColor: !themeDarkMode ? '#DAE1E9' : '#4D5D6D' },
    DIVIDER: { borderColor: !themeDarkMode ? '#D8D8D8' : '#848484' },
    SUCCESS: {
      borderColor: !themeDarkMode ? '#DAE1E9' : '#4D5D6D',
      total: {
        color: themeDarkMode ? '#cfcfcf' : '#353946',
      },
      item: {
        color: themeDarkMode ? '#cfcfcf' : '#333',
      },
    },
    TERMS: { color: !themeDarkMode ? '#232323' : '#AFB0B1' },
  };
};

export const getSideBarNav = (): any => {
  const themeDarkMode = isDarkMode();

  return {
    bgGradient: themeDarkMode ? '#212121' : 'linear-gradient(180deg, #E4F8F6 0%, #FFFFFF 100%)',
  };
};
